import { serverHost } from "../../../../../http_common.js";
import { useState, useRef, useEffect } from "react";
import "./payments.css"; // Import the CSS file
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import axios from "axios";
import Logo from "../test.png";
import AssuredWorkloadIcon from "@mui/icons-material/AssuredWorkload";


export default function Payments() {
  const { accessType } = useSelector((state) => state.clientFunction);
  const companyData = useSelector(
    (state) => state.clientFunction?.companiesDB?.clientData,
  );
  const staffData = useSelector((state) => state.clientFunction?.companiesDB);
  const { registerData } = useSelector((state) => state.terminalActions);

  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [transactionsData, setTransactionsData] = useState([]);
  const [loadError, setLoadError] = useState("");
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  async function apiGetSales() {
    if (!registerData?.registerId || !companyData?.clientId) return;
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await axios.get(`${serverHost}/register/${encodeURIComponent(registerData.registerId)}/client/${encodeURIComponent(companyData.clientId)}/getRegSoldItem`, { timeout: 20000 });
      if (![200, 201].includes(data?.status)) throw new Error(data?.message || "Unable to load register payments.");
      setTransactionsData(Array.isArray(data?.regTransactions) ? data.regTransactions : []);
    } catch (error) {
      setLoadError(error.response?.data?.message || error.message || "Unable to load payments.");
      setTransactionsData([]);
    } finally { setLoading(false); }
  }

  // SUMMATION OF THE ENTIRE TRANSACTIONS FUCTIONS
  function getSalesSummary(rows = []) {
    const methods = ["cash", "credit", "debit", "gift", "account", "cheque", "multiple-pay"];
    const summary = {
      totalItemsSold: 0, totalPaymentsMade: 0, totalDebts: 0,
      totalSales: 0, totalTax: 0, totalDiscount: 0, cashCollected: 0,
      paymentMethods: Object.fromEntries(methods.map(m => [m, { transactions: 0, amount: 0 }])),
    };
    for (const original of rows) {
      const transaction = original?.soldItemPayload || original;
      if (!transaction) continue;
      const total = Number(transaction.totals?.total ?? transaction.totals?.subtotal ?? 0);
      if (!Number.isFinite(total)) continue;
      const methodRaw = String(transaction.payment?.method || "").toLowerCase();
      const method = methodRaw === "check" ? "cheque" : methodRaw === "multiple" ? "multiple-pay" : methodRaw;
      summary.totalItemsSold += (transaction.soldItems || []).reduce((sum, item) => sum + Number(item.soldQuantity || 0), 0);
      summary.totalSales += total;
      summary.totalTax += Number(transaction.totals?.taxAmount || 0);
      summary.totalDiscount += Number(transaction.totals?.discountAmount || 0);
      if (summary.paymentMethods[method]) {
        summary.paymentMethods[method].transactions += 1;
        summary.paymentMethods[method].amount += total;
      }
      if (method === "multiple-pay") {
        for (const payment of transaction.payment?.payments || []) {
          const amount = Number(payment?.amount || 0);
          if (!Number.isFinite(amount) || amount < 0) continue;
          const option = String(payment?.method || "").toLowerCase();
          if (option === "credit") summary.totalDebts += amount;
          else if (["cash", "account", "debit"].includes(option)) summary.totalPaymentsMade += amount;
          if (option === "cash") summary.cashCollected += amount;
        }
      } else if (method === "credit") summary.totalDebts += total;
      else if (["cash", "account", "debit"].includes(method)) summary.totalPaymentsMade += total;
      if (method === "cash") summary.cashCollected += total;
    }
    return summary;
  }
  const salesSummary = getSalesSummary(transactionsData);

  // SESSION FUNCTIONS

  const diffMs = Math.max(0, Date.now() - new Date(registerData?.timeInn || Date.now()).getTime());

  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  const sessionTime = `${hours}h ${minutes}m`;

  const sessionStart = new Date(registerData?.timeInn || Date.now());
  const from = sessionStart.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

  const to = new Date().toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

  const theSession = `${from} - ${to}`;
  useEffect(() => {
    apiGetSales();
  }, [registerData?.registerId, companyData?.clientId]);
  return (
    <div id="paymentsTransactions" className="paymentsWrapper fx-cl space3" onClick={e => e.stopPropagation()}>
      <div className="pos-report-status"><span>{loading ? "Loading payments…" : `${transactionsData.length} register transactions`}</span><button disabled={loading} onClick={apiGetSales}>Refresh</button>{loadError && <p role="alert" className="pos-inline-error">{loadError}</p>}</div>
      <div className="payments flex-container">
        {/* First Container */}
        <div className="payments-first-container">
          <div className="payments-left-side fx-ac space2">
            <figure className="paymentsLogo">
              <AssuredWorkloadIcon style={{ fontSize: "4.7rem" }} />
            </figure>
            <div className="fx-cl space1">
              <h1>Payments</h1>

              <p>Representative: {registerData?.representiveName}</p>
            </div>
          </div>
          <div className="payments-right-side fx-cl space1">
            <p>{registerData?.location?.name} </p>
            <p>{registerData?.location?.address}</p>
          </div>
        </div>

        {/* Second Container */}
        <div className="payments-second-container fx-jb space3">
          <div className="payments-inner-div">
            <div className=" fx-cl space1">
              <h3>Operation Details</h3>
              <div className="fx-cl spacem">
                <p>
                  <strong>Register Reference ID: </strong>
                  {registerData?.registerId}
                </p>
                <p>
                  <strong>Items sold: </strong>
                  {salesSummary.totalItemsSold}
                </p>
                <p>
                  <strong>Total payments: </strong> ₦
                  {salesSummary.totalPaymentsMade.toLocaleString()}
                </p>
                <p>
                  <strong>Debts: </strong> ₦
                  {salesSummary.totalDebts.toLocaleString()}
                </p>
                <p>
                  <strong>Session: </strong>
                  {theSession}
                </p>
                <p>
                  <strong>Hours: </strong> {sessionTime}
                </p>
              </div>
            </div>
          </div>
          <div className="payments-inner-div">
            <div className=" fx-cl space1">
              <h3>User info:</h3>
              <div className="fx-cl spacem">
                <p>
                  <strong>Staff ID: </strong> {registerData?.representiveId}
                </p>
                <p>
                  <strong>Username: </strong>
                  {registerData?.representiveUserName}
                </p>

                <p>
                  <strong>Location: </strong> {registerData?.location?.name}
                </p>
                <p>
                  <strong>Phone No.: </strong>{" "}
                  {registerData?.representiveNumber}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Third Container */}
        <div className="payments-third-container fx-cl space3">
          <div className="fx-cl">
            <h3>Payment details</h3>
            <p>Overview of payments and outstanding balances</p>
          </div>
          <table className="paymentsTable">
            <thead style={{ textAlign: "left" }}>
              <tr>
                <th>Serial No.</th>
                <th>Items</th>
                <th>Description</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr><td colSpan="4"><h2>Payment Methods</h2></td></tr>

              {Object.entries(salesSummary.paymentMethods).map(
                ([method, data], index) => (
                  <tr key={method}>
                    <td>{String(index + 1).padStart(2, "0")}</td>
                    <td>
                      <strong>
                        {method.replace(/\b\w/g, (char) => char.toUpperCase())}
                      </strong>
                    </td>
                    <td>{data.transactions} transaction(s)</td>
                    <td>₦{data.amount.toLocaleString()}</td>
                  </tr>
                ),
              )}

              <tr>
                <td></td>
                <td>
                  <strong>TOTAL</strong>
                </td>
                <td>-</td>
                <td>
                  <strong>
                    ₦{salesSummary.totalPaymentsMade.toLocaleString()}
                  </strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
